import React, { PropTypes, Component } from 'react';
import ReactDOM from 'react-dom';
import { connect, Provider } from 'react-redux';
import { push } from 'react-router-redux';
import Helmet from 'react-helmet';
import { Flags, AppEnvs, RenderTemplateTypes } from 'enums';
import canUseDOM from 'lib/util/canUseDOM';
import {
    fetchExternalConfig,
    analyticsReady,
    contentUnSelect,
    streamUnSelect,
    mediaQuery
} from 'common/actions/appActions';
import { scoreAll } from 'common/actions/scoringActions';
import { intlShape } from 'common/injectIntl';
import { clearPage, createPageShellInitAction, refreshBuckets } from 'common/page/actions';
import { createClearPlayingStreamAction, createResetPlayerAction, createFeedResetAction } from 'webapp/player/actions';
import connectAdInitializer from 'common/ads/connectAdInitializer';
import bootstrapSelector, { espnPlayerSrcFilesSelector } from 'common/selectors/bootstrapSelector';
import appSelector, { isSecureSelector, isIntlSelector } from 'common/selectors/appSelector';
import { bamSessionTokenSelector } from 'common/selectors/authenticationSelectors';
import playerSelector from 'common/selectors/playerSelector';
import routingSelector from 'common/selectors/routingSelector';
import pageSelector from 'common/selectors/pageSelector';
import { persistentFlagsSelector } from 'common/selectors/flagsSelector';
import BrandingContainer from 'webapp/branding/BrandingContainer';
import ProviderModal from 'webapp/modal/authn/ProviderModal';
import MessagingContainer from 'webapp/modal/messaging/MessagingContainer';
import { addQueryToUrl } from 'lib/format';
import { subscribeToStore } from 'lib/observe';
import scoringObserver from 'lib/observe/scoringObserver';
import trackingObserver from 'lib/observe/trackingObserver';
import { uiLoadingStart } from 'lib/ui/loading';
import { get } from 'lib/util/localStorage';
import {
    loadingImageUrl,
    secureLoadingImageUrl,
    loadingImageUrlDark,
    BAM_PROXY_IFRAME_ID,
    BAM_PROMO_IFRAME_ID,
    BAM_PAYWALL_IFRAME_ID,
    SESSION_TOKEN_KEY
} from 'constants';
import IntlProvider from 'common/IntlProvider';
import appConfig from '../../../config';

const {
    shellVersion,
    secureCdnHost,
    devStyles,
    clientNavigation
} = appConfig;

const STYLE_HREF = devStyles ? 'http://localhost:8080/static/css' : `${secureCdnHost}/redesign/${shellVersion}/css/secure`;

// sanitize anchor tags in nav (don't ajax, and remove useless href)
function sanitizeAnchor(anchor) {
    if (anchor instanceof HTMLAnchorElement) {
        if (anchor.getAttribute('href') === '#') {
            anchor.removeAttribute('href');
        }
        anchor.setAttribute('data-route', 'false');
    }
}

function initializeEspnChat() {
    const chatLink = document.querySelector('#global-nav-secondary .global-nav-container ul li a[data-abbrev=chatwidget]');

    if (chatLink) {
        const htmlHead = document.getElementsByTagName('head')[0];
        const chatScript = document.createElement('script');
        chatScript.type = 'text/javascript';
        chatScript.src = 'https://c.la4-c2-chi.salesforceliveagent.com/content/g/js/43.0/deployment.js';
        chatScript.onload = () => {
            window.liveagent.init('https://d.la4-c2-chi.salesforceliveagent.com/chat', '5721a000000055c', '00D1a000000JpU6');
            if (!window._laq) {
                window._laq = [];
            }
            window._laq.push(() => {
                window.liveagent.showWhenOnline('5731a00000005l3', chatLink);
            });
        };
        // Append the liveagent script to the head tag
        htmlHead.append(chatScript);

        // Add click handler to open the chat window
        chatLink.setAttribute('id', 'liveagent_button_online_5731a00000005l3');
        chatLink.onclick = ((event) => {
            event.preventDefault();
            if (window.liveagent && window.liveagent.startChat) {
                window.liveagent.startChat('5731a00000005l3');
            }
        });
    } else {
        console.warn('Unable to initialize chat widget, selector is undefined.');
    }
}

// TODO: DTC - remove this once we have it in the nav
function insertNavItems(navItems) {
    if (!window.jQuery) {
        return;
    }

    function insertItem(index, route, label) {
        if (!document.querySelector(`#global-nav-secondary .global-nav-container ul li a[href*="/watch/${route}"]`)) {
            const navList = document.querySelectorAll('#global-nav-secondary .global-nav-container ul li');
            if (navList) {
                window.jQuery(`
                    <li class="" itemprop="name">
                        <a itemprop="url" href="http://www.espn.com/watch/${route}/" name="&lpos=sitenavcustom_watch_${route}" data-route="false" data-abbrev="watch">
                            <span class="link-text">${label}</span>
                            <span class="link-text-short">${label}</span>
                        </a>
                    </li>
                `).insertAfter(window.jQuery(navList).get(index));
            }
        }
    }

    if (navItems && navItems.length) {
        navItems.forEach(({ index, label, path }) => insertItem(index, path, label));
    }
}

function updateSubNav(location, navItems) {
    if (navItems) {
        insertNavItems(navItems);
    }
    // updateSubNav is called only on the client side.
    if (location && window.jQuery) {
        window.jQuery('#global-nav-secondary li').each(function () { // eslint-disable-line
            const $this = window.jQuery(this);
            const href = $this.find('a[itemprop="url"]').attr('href');

            if (
                `${location.basename}${location.pathname}` === href ||
                `${location.basename}${location.pathname}/` === href
            ) {
                $this.addClass('active');
            } else if ($this.hasClass('active')) {
                $this.removeClass('active');
            }
        });
    }
}

class PageShell extends Component {
    constructor(props) {
        super(props);
        this.mountBrandingComponent = this.mountBrandingComponent.bind(this);
        this.insertProviderContainer = this.insertProviderContainer.bind(this);
        this.providerContainer = null;
        this.subNavEl = null;
        this.latestLocation = null;
    }

    componentWillMount() {
        const { bootstrap: { pageShell: { nav } = {} }, isIntl, navOverride } = this.props;

        if (canUseDOM) {
            // this disables the shell from rendering ads (so that we can do it)
            // see https://code.espn.com/webdev/score/blob/ec82c07f8a607971029786f21fffbb211996e370/static/js/ads/init.js#L358
            if (window.espn_ui) {
                window.espn_ui.pageShell = true;
            }

            const { store, router } = this.context;
            const { dispatch } = this.props;

            this.unsubscribeFromStore = subscribeToStore(store, [
                scoringObserver,
                trackingObserver
            ]);

            const unlistenBefore = router.listenBefore((newLocation) => {
                const { player, routing } = this.props;
                if (player.embedding === true) {
                    return false;
                }

                // if the pathname is different, clear the redux state
                const { locationBeforeTransitions: currentLocation = {} } = routing;
                const currentPath = `${currentLocation.basename}${currentLocation.pathname}`;
                const newPath = `${newLocation.basename}${newLocation.pathname}`;
                const currentPathRegex = new RegExp(`^${currentPath}\/?$`);

                if (!newPath.match(currentPathRegex)) {
                    dispatch(contentUnSelect());
                    dispatch(streamUnSelect());
                    dispatch(createClearPlayingStreamAction());
                    dispatch(createResetPlayerAction());
                    dispatch(clearPage());
                    dispatch(createFeedResetAction());
                }
                return true;
            });

            const unlisten = router.listen((location) => {
                this.latestLocation = location;

                // this fixes broken scrolling when using the back and forward browser buttons btw ajax pages
                if (window.espn_ui && window.espn_ui.Helpers) {
                    window.espn_ui.Helpers.history.updateState('react', true);
                }

                updateSubNav(location, navOverride && nav);
            });

            this.unlisten = () => {
                unlistenBefore();
                unlisten();
            };

            if (isIntl) {
                this.initPageShell();
            }
        }
    }

    componentDidMount() {
        const { dispatch, bootstrap: { pageShell: { nav } = {} }, flags, isIntl, navOverride } = this.props;
        if (!isIntl) {
            this.initPageShell();
        }
        if (window.jQuery) {
            if (window.jQuery.subscribe) {
                window.jQuery.subscribe('userBuilt', () => {
                    setTimeout(() => {
                        const profileAnchorTags = document.querySelectorAll('.user a');
                        Array.prototype.forEach.call(profileAnchorTags, (anchor) => {
                            sanitizeAnchor(anchor);
                        });
                    }, 0);
                });
                window.jQuery.subscribe('analytics.loaded', (initData) => {
                    dispatch(analyticsReady(initData));
                });
                window.jQuery.subscribe('espn.defer.end', () => {
                    // noop `FastClick.attach` before it gets called here to fix the button click issue on mobile
                    // https://code.espn.com/webdev/score/blob/develop/static/js/espn-ui.js#L296
                    if (window.FastClick) {
                        window.FastClick.attach = () => {};
                    }
                });
            }

            window.jQuery(window).on('media_query_updated', () => {
                if (window.espn_ui && window.espn_ui.Helpers) {
                    const nextMq = window.espn_ui.Helpers.get_media_query_in_use();
                    dispatch(mediaQuery(nextMq));
                }
            });
            if (window.espn_ui && window.espn_ui.Helpers) {
                const currentMq = window.espn_ui.Helpers.get_media_query_in_use();
                dispatch(mediaQuery(currentMq));
            }

            // NOTE: this may not fire locally on all browsers
            // 'nav.ready' will be fired not only the initial load but also every break point change
            window.jQuery(document).on('nav.ready', () => {
                window.jQuery('#global-nav > ul > li.watch').addClass('active');
                const headerAnchorTags = document.querySelectorAll('#header-wrapper a');
                Array.prototype.forEach.call(headerAnchorTags, (anchor) => {
                    sanitizeAnchor(anchor);
                });
                updateSubNav(this.latestLocation, navOverride && nav);

                // Append the Chat subnav within FAQ nav item only for Domestic
                if (!isIntl) {
                    initializeEspnChat();
                }

                this.mountBrandingComponent(); // in the event the nav completes after auth
            });
        }

        if (window.espn && window.espn.refresh) {
            window.espn.refresh.stop();
        }

        if (clientNavigation || flags[Flags.CLIENT_NAV] === 'true') {
            const secondaryNavContainer = document.getElementById('global-nav-secondary');
            if (secondaryNavContainer) {
                secondaryNavContainer.addEventListener('click', (event) => {
                    this.interceptNavClick(secondaryNavContainer, event);
                });
            }
        }
    }

    componentWillReceiveProps(nextProps) {
        const { location = {} } = nextProps;
        const currentLocation = this.props.location;
        if (currentLocation !== location) {
            // add playback specific class to body on playback page
            if (location.pathname.indexOf('/player') !== -1) {
                if (document.body.className.indexOf('playback') === -1) {
                    document.body.className = `${document.body.className} watch--playback`;
                }
            } else {
                document.body.className = document.body.className.replace(/\s?watch--playback/, '');
            }
        }

        // media queries dirty the dom in the subnav, listen for them and clean it up
        if (this.props.mq && (this.props.mq !== nextProps.mq) && this.providerContainer) {
            // unmount the branding component entirely followed by mounting it again by 'nav.ready'
            ReactDOM.unmountComponentAtNode(this.providerContainer);
            if (this.subNavEl) {
                this.subNavEl.removeChild(this.providerContainer);
                this.providerContainer = null;
            }
        }

        this.handleSessionTokenPageRefetch(nextProps);
    }

    componentWillUnmount() {
        if (this.unlisten) {
            this.unlisten();
        }
        if (this.unsubscribeFromStore) {
            this.unsubscribeFromStore();
        }
    }

    initPageShell() {
        const { dispatch, params } = this.props;
        dispatch(createPageShellInitAction(params, BAM_PROXY_IFRAME_ID));
    }

    interceptNavClick(root, event, parent) {
        const target = parent || event.target;
        if (target === root) {
            return;
        }

        const { flags } = this.props;
        const href = target.getAttribute && target.getAttribute('href');
        // exclude feedback page from client render since it uses basic shell
        if (href && href.indexOf('/watch/') > 0 && href.indexOf('/watch/feedback') === -1) {
            event.preventDefault();
            this.context.store.dispatch(
                push(addQueryToUrl(href.split('/watch/')[1], flags))
            );
        } else if (target.parentNode) {
            this.interceptNavClick(root, event, target.parentNode);
        }
    }

    handleSessionTokenPageRefetch(nextProps = {}) {
        const { sessionToken, pageId, params } = nextProps;
        const { dispatch, sessionToken: initialSessionToken } = this.props;
        const localStoredSessionToken = get(SESSION_TOKEN_KEY);
        try {
            // NOTE: Refetches the Page buckets if the sessionToken is updated in the store
            // and the initialSessionToken is undefined along with the localStorage sessionToken.
            // The sessionToken is always re-assigned on any client transition or page load.
            // Does not impact routes with /schedule or /player
            if (!initialSessionToken
                && sessionToken
                && !localStoredSessionToken
                && ['schedule', 'player'].indexOf(pageId) === -1) {
                dispatch(refreshBuckets(pageId, { params, forceRefresh: true }));
            }
        } catch (error) {
            console.error(`handleSessionTokenPageRefetch() Error: Failed to refresh buckets ${pageId}`);
        }
    }

    mountBrandingComponent() {
        const { store } = this.context;
        this.insertProviderContainer();
        if (this.providerContainer) {
            ReactDOM.render(
                <Provider store={store}>
                    <IntlProvider>
                        <BrandingContainer />
                    </IntlProvider>
                </Provider>,
                this.providerContainer
            );
        }
    }

    // This is a hack to insert DOM into the nav element to hold the provider logo.  It will go away
    insertProviderContainer() {
        if (this.subNavEl === null) {
            this.subNavEl = document.querySelector('#global-nav-secondary .global-nav-container');
        }
        if (!this.subNavEl) {
            return;
        }

        if (!document.querySelector('#provider-logo-subnav')) {
            const providerLogo = document.createElement('div');
            providerLogo.id = 'provider-logo-subnav';
            this.providerContainer = this.subNavEl.appendChild(providerLogo);
        }
    }

    render() {
        const {
            isSecure,
            espnPlayerSrc: {
                espnPlayerJsUrl,
                espnPlayerCssUrl
            }
        } = this.props;

        return (
            <div>
                <Helmet
                    link={[
                        { rel: 'stylesheet', href: `${STYLE_HREF}/shell-desktop.css` },
                        { rel: 'stylesheet', href: `${STYLE_HREF}/page-shell.css` },
                        { class: 'page-type-include', rel: 'stylesheet', href: `${STYLE_HREF}/index.css` },
                        { rel: 'stylesheet', href: `${STYLE_HREF}/watch.css` },
                        { rel: 'stylesheet', href: `${STYLE_HREF}/watch-provider.css` },
                        { rel: 'stylesheet', href: `${STYLE_HREF}/watch-stream-picker.css` },
                        {
                            class: '__watch-player-bundle__',
                            rel: 'stylesheet',
                            href: espnPlayerCssUrl
                        }
                    ]}
                    style={[{
                        cssText: `
                            audio::-internal-media-controls-cast-button, video::-internal-media-controls-cast-button {
                                display: none;
                            }

                            .no-header #main-container { padding-top: 0; }

                            @media screen and (min-width: 1024px) {
                                .collection.collection--watch .ad-300 {
                                    margin-left: 20px;
                                }
                                .playback__content .collection_item>.collection__link {
                                    padding-left: 20px;
                                    padding-right: 20px;
                                }
                            }
                            /* TODO: remove once we have it in css */
                            #global-nav-secondary .global-nav-container .sportbrand .sportbrand__watch>img {
                                max-height: 25px;
                            }

                            /* Carousel Hero CSS */
                            .dtc-watch__carousel--container {
                                position: relative;
                                width: 100%;
                            }

                            .dtc-watch__carousel--container.carousel-loading .dtc-watch__carousel {
                                display: none;
                            }

                            .dtc-watch__carousel--container.carousel-loading {
                                margin-bottom: 44px;
                            }

                            .dtc-watch__carousel--container.carousel-loading:before {
                                background:url("${secureLoadingImageUrl}") 50% 50% no-repeat #dcdddf !important;
                                background-size:cover !important;
                                bottom:0;
                                content:'';
                                display:block;
                                left:0px;
                                opacity:1 !important;
                                position:absolute;
                                right:0px;
                                top:0;
                                z-index:3 !important;
                            }

                            @media screen and (max-width: 767px) {
                                .dtc-watch__carousel--container,
                                .dtc-watch__carousel__item img {
                                    min-height: 40vw;
                                }
                            }

                            @media screen and (min-width: 768px) and (max-width: 1023px) {
                                .dtc-watch__carousel--container,
                                .dtc-watch__carousel__item img {
                                    min-height: 296px;
                                }
                            }

                            @media screen and (min-width: 1024px) and (max-width: 1259px) {
                                .dtc-watch__carousel--container,
                                .dtc-watch__carousel__item img {
                                    min-height: 392px;
                                }
                            }

                            @media screen and (min-width: 1260px) {
                                .dtc-watch__carousel--container,
                                .dtc-watch__carousel__item img {
                                    min-height: 488px;
                                }
                            }

                            .dtc_carousel__dots { display: flex !important; }

                            #watchespn-bam-player {
                                background-color: #000 !important;
                                height: 100% !important;
                                left: 0;
                                position: absolute;
                                top: 0;
                                width: 100% !important;
                                z-index: 1000031;
                            }

                            #watchespn-player iframe,
                            #watchespn-bam-player iframe{
                                width: 100%;
                                height: 100%;
                                border: none;
                            }

                            .${BAM_PROXY_IFRAME_ID}, .${BAM_PROXY_IFRAME_ID} iframe {
                                position: absolute;
                                left: -10000px;
                                width: 1px;
                                height: 1px;
                                border: none;
                            }

                            #${BAM_PROMO_IFRAME_ID} {
                                width: 100%;
                                height: 506.25px;
                                margin-bottom: 10px;
                            }

                            .collection_item_truncate>span, .hero_truncate>span {
                                font-family: BentonSans, -apple-system, Roboto, Helvetica, Arial, sans-serif;
                            }

                            #${BAM_PROMO_IFRAME_ID} iframe,
                            #${BAM_PAYWALL_IFRAME_ID} iframe {
                                width: 100%;
                                height: 100%;
                                border: none;
                                display: block;
                            }

                            .lightbox-container .lightbox-watch.watch-provider-modal.lightbox-paywall .btn-close {
                                padding: 0;
                            }

                            .lightbox-container .lightbox-watch.watch-provider-modal.lightbox-paywall .btn-close:before {
                                color: #fff;
                                line-height: 36px;
                            }

                            body.ReactModal__Body--open {
                                overflow: hidden;
                            }

                            .video-overlays-container {
                                position: absolute;
                                top: 0;
                                left: 0;
                                width: 100%;
                                height: 100%;
                            }
                            .streampicker.overlay {
                                width: 100%;
                                height: 100%;
                            }
                            .StreamPicker__Container {
                                height: auto;
                                top: auto;
                            }
                            .video-player__stream {
                                position: relative;
                                height: 100%;
                                display: flex;
                                justify-content: center;
                                align-items: center;
                            }
                            .StreamPicker__Header {
                                border-bottom: #48494a 1px solid;
                                height: 50px;
                                position: relative;
                                text-align: center;
                            }
                            .StreamPicker__Body {
                                padding: 16px;
                                max-height: 253px;
                                overflow-y: scroll;
                            }
                            .StreamPicker__Broadcast {
                                margin-bottom: 12px;
                                margin-top: 0;
                            }
                            .StreamPicker__Row {
                                margin-bottom: 18px;
                            }
                            .StreamPicker__Row:last-child {
                                margin-bottom: 0;
                            }
                        `
                    }]}
                    script={[
                        {
                            src: '//a.espncdn.com/combiner/c?js=analytics/visitorAPI_312.js'
                        },
                        {
                            src: espnPlayerJsUrl,
                            async: true
                        }
                    ]}
                />
                {/* Get the image as early as possibly, essentially an attempt to pre-fetch */}
                <img
                    alt="prefetch loading graphic"
                    src={isSecure ? secureLoadingImageUrl : loadingImageUrl}
                    style={{ display: 'none' }}
                />
                {isSecure ? null : (<img
                    alt="prefetch loading graphic"
                    src={loadingImageUrlDark}
                    style={{ display: 'none' }}
                />)}
                <ProviderModal />
                <MessagingContainer />
                {this.props.children}
            </div>
        );
    }
}

PageShell.propTypes = {
    dispatch: PropTypes.func.isRequired,
    children: PropTypes.node,
    isSecure: PropTypes.bool,
    isIntl: PropTypes.bool,
    bootstrap: PropTypes.object,
    location: PropTypes.object,
    mq: PropTypes.string,
    navOverride: PropTypes.bool,
    player: PropTypes.object,
    routing: PropTypes.object,
    flags: PropTypes.object,
    params: PropTypes.object,
    sessionToken: PropTypes.string,
    espnPlayerSrc: PropTypes.object
};
PageShell.contextTypes = {
    store: PropTypes.object.isRequired,
    router: PropTypes.object.isRequired,
    intl: intlShape.isRequired,
    messages: PropTypes.object.isRequired
};

PageShell.renderTemplate = RenderTemplateTypes.PAGE_SHELL;
PageShell.preActions = [fetchExternalConfig];
PageShell.loadActions = [];
PageShell.onLoadStart = uiLoadingStart;
PageShell.onComplete = function onComplete(dispatch) {
    dispatch(scoreAll());
};

const mapStateToProps = (state) => {
    const bootstrap = bootstrapSelector(state);
    const mq = appSelector(state).mq;
    const lang = appSelector(state).lang;
    const env = appSelector(state).env;
    const player = playerSelector(state);
    const routing = routingSelector(state);
    const flags = persistentFlagsSelector(state);
    const isSecure = isSecureSelector(state);
    const isIntl = isIntlSelector(state);
    const { id: pageId } = pageSelector(state);
    const sessionToken = bamSessionTokenSelector(state);
    const espnPlayerSrc = espnPlayerSrcFilesSelector(state);
    return {
        bootstrap,
        mq,
        navOverride: !isIntl && env !== AppEnvs.prod,
        player,
        routing,
        flags,
        isSecure,
        isIntl,
        sessionToken,
        lang,
        pageId,
        espnPlayerSrc
    };
};

export default connect(mapStateToProps)(connectAdInitializer(PageShell));



// WEBPACK FOOTER //
// ./app/webapp/subShell/PageShell.js